/**
 * Twin patterns resource — typed wrappers over `/v1/twin/patterns` plus the
 * co-located React Query hooks (foundry genealogy CAPTURE, P-capture).
 *
 * A "pattern" is the tooling that shapes a casting: a pattern / die / core box /
 * match plate / shell die, carrying one or more cavities. Each cavity names the
 * part it produces and its designed weight, so a pour can attribute a casting to
 * a specific cavity. The list + upsert + delete back the Patterns admin screen
 * (`routes/twin/patterns.tsx`); the list also feeds the Log-Pour cavity picker.
 *
 * Mirrors the typed-wrapper shape of the other twin-* modules (snake_case wire
 * shapes, `api` client from api-client). Hooks are gated on the live API + an
 * authenticated, org-resolved session exactly like queries.ts, so they no-op in
 * stub mode and never fire before the X-Org-Id header is wired.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { api } from "@/lib/api-client";

/** Tooling class a pattern represents. */
export type PatternToolClass =
  | "pattern"
  | "die"
  | "core_box"
  | "match_plate"
  | "shell_die";

/** Lifecycle state of a pattern. */
export type PatternStatus = "active" | "retired" | "maintenance";

/**
 * One cavity on a pattern — the impression that yields one casting per pour.
 *
 * `cavity_volume_cm3` / `section_modulus_mm` are the migration-0113 RESERVED
 * geometry columns. The read handler's cavity SELECT (`internal/twin/patterns.go`)
 * now returns both as `*float64` without `omitempty`, so an unpopulated column
 * arrives as an explicit `null` — RESERVED means "nothing writes them yet", not
 * "the API hides them". Still typed optional: `undefined` and `null` are the same
 * withhold to the UI ("—", never 0).
 */
export interface PatternCavity {
  cavity_no: number;
  label?: string;
  part_number?: string;
  /**
   * Nominal per-casting metal for this impression, kg. NULLABLE on the wire —
   * Go emits it as `*float64` with no `omitempty`, so an un-weighed cavity
   * arrives as an explicit `null`. Withhold ("—") on null; never read it as 0.
   */
  designed_weight_kg?: number | null;
  /** mig 0113 RESERVED — future CALCULATED = designed_weight_kg / rho_solid. */
  cavity_volume_cm3?: number | null;
  /** mig 0113 RESERVED, DO NOT POPULATE — the Stage-3 cooling term (P16/P17). */
  section_modulus_mm?: number | null;
}

/**
 * A pattern + its cavities (the full read/upsert shape).
 *
 * The block below the 0060 fields is migrations **0112** (rigging/yield) and
 * **0113** (identity, freshness, core-box spec). Every one is nullable in the DB,
 * and all of them are now RETURNED by the widened read handler as pointers
 * without `omitempty` (2026-08-01), so an unset column arrives as an explicit
 * `null`. `undefined` (older backend) and `null` (unset) both mean the same thing
 * to the UI: withhold, render "—", never 0.
 *
 * THE WRITE SIDE EXISTS since 2026-08-02 (WS-C#1): `PATCH
 * /v1/twin/patterns/{id}/tooling`, in `./twin-tooling-specs`. This comment used to
 * say no PATCH endpoint existed — true when it was written, false the day the
 * endpoint shipped, and exactly the class of stale claim the connectivity build's
 * static gate hunts. `POST /v1/twin/patterns` (below) is still a whole-body upsert
 * and still does NOT touch the 0112/0113 spec columns; the PATCH is their writer.
 *
 * `cores_per_casting` here is the DEMOTED FALLBACK for the core count (council
 * ruling 1, 2026-08-02): the authoritative home is the pattern↔core-box bill,
 * `PUT /v1/twin/patterns/{id}/core-boxes` (WS-C#2c, also in twin-tooling-specs).
 */
export interface Pattern {
  id: string;
  code: string;
  display_name: string;
  part_number?: string;
  tool_class: PatternToolClass;
  cavity_count: number;
  status: PatternStatus;
  cavities: PatternCavity[];

  // ── mig 0091 — the product this pattern makes ──
  /**
   * Which product (part) this tooling produces. Soft ref, org-checked on the
   * server; the recipe layer reads it to imply a product from a picked pattern.
   * NULL/undefined = unassigned tooling (render "—", never invent one).
   */
  product_id?: string | null;

  // ── mig 0112 — per-MOULD rigging (read by the forward-sizing emitter) ──
  /** Gating+riser mass, counted ONCE per mould (never per cavity). kg. */
  rigging_mass_kg?: number | null;
  /** DESIGN casting yield as a percent (68 = 68%). Never the achieved yield. */
  yield_pct?: number | null;
  /** mig 0112 RESERVED (not read yet) — loose-gating / no-bake separate tool. */
  gating_tool_id?: string | null;
  /** mig 0112 RESERVED (not read yet) — ductile riser mass, distinct from runner. */
  riser_wt_kg?: number | null;

  // ── mig 0113 — tool identity + spec freshness ──
  /** Tool revision, carried FROZEN as-of-pour. */
  pattern_rev?: string | null;
  /** WHICH physical pattern / match-plate copy (one design, many instances). */
  physical_instance_id?: string | null;
  /** RFC3339. When the reserved specs were last green-confirmed; null = never. */
  confirmed_at?: string | null;

  // ── mig 0113 — core-box spec (rides a tool_class='core_box' row) ──
  /** Designed weight per core, kg. No universal default — withhold if unset. */
  core_weight_kg?: number | null;
  /** RESERVED — future CALCULATED = core_weight_kg / rho_core. */
  core_volume_cm3?: number | null;
  /** Core-sand class: silica / cold-box PU / chromite / zircon (free text). */
  core_sand_type?: string | null;
  /** How many of THIS core go into one casting (DB CHECK 0–8). */
  cores_per_casting?: number | null;
}

/** List envelope — the endpoint returns `{ items }` (may be a bare array). */
export interface PatternList {
  items: Pattern[];
}

/** Upsert body — same shape as the read (id optional; the server mints one). */
export interface PatternUpsert {
  id?: string;
  code: string;
  display_name: string;
  part_number?: string;
  tool_class: PatternToolClass;
  cavity_count: number;
  status: PatternStatus;
  cavities: PatternCavity[];
  /**
   * The product this pattern makes. Send "" to clear the link, an id to set it
   * (server org-validates), or omit the key to leave any existing link untouched.
   */
  product_id?: string | null;
}

export const twinPatterns = {
  /** Every pattern for the active org. */
  list: () => api.get<PatternList>("/v1/twin/patterns"),
  /** One pattern by id (with its cavities). */
  get: (id: string) => api.get<Pattern>(`/v1/twin/patterns/${id}`),
  /** Create or update a pattern (upsert). */
  upsert: (body: PatternUpsert) => api.post<Pattern>("/v1/twin/patterns", body),
  /** Remove a pattern. */
  remove: (id: string) => api.delete<void>(`/v1/twin/patterns/${id}`),
};

/** Build-time flag: only when explicitly "true" do live fetches fire. */
const isLiveApi = import.meta.env.VITE_USE_LIVE_API === "true";

/**
 * Live fetches require the build flag AND an authenticated, org-resolved
 * session — same gate as queries.ts::useLiveEnabled (kept local so this module
 * stays additive and free of a circular import on queries.ts).
 */
function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApi && isAuthenticated && activeOrgId !== null;
}

/** Query key for the pattern list — exported so mutations can invalidate it. */
export const TWIN_PATTERNS_KEY = ["twin-patterns"] as const;

/** The active org's patterns (drives the Patterns screen + pour cavity picker). */
export function usePatternsQuery() {
  return useQuery({
    queryKey: TWIN_PATTERNS_KEY,
    queryFn: () => twinPatterns.list(),
    enabled: useLiveEnabled(),
    staleTime: 60_000,
  });
}

/** One pattern, fetched on demand (edit Sheet open). Disabled until an id is set. */
export function usePatternQuery(id: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: [...TWIN_PATTERNS_KEY, id],
    queryFn: () => twinPatterns.get(id as string),
    enabled: live && !!id,
    staleTime: 60_000,
  });
}

/** Create / update a pattern; invalidates the list on success. */
export function useUpsertPattern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatternUpsert) => twinPatterns.upsert(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_PATTERNS_KEY });
    },
  });
}

/** Delete a pattern; invalidates the list on success. */
export function useDeletePattern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinPatterns.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TWIN_PATTERNS_KEY });
    },
  });
}
